import { useState } from 'react';
import { FileText, Settings, CheckCircle, Edit, Eye, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MethodologyStepModal from './MethodologyStepModal';

interface Step {
  id: number;
  title: string;
  shortTitle: string;
  icon: typeof FileText;
  color: string;
  bgGradient: string;
  description: string;
  details: {
    title: string;
    content: string[];
    keyPoints: string[];
    governance?: string;
    important?: string;
  };
}

const steps: Step[] = [
  {
    id: 1,
    title: 'Création gabarits',
    shortTitle: 'Création gabarits',
    icon: Settings,
    color: 'from-blue-500 to-indigo-600',
    bgGradient: 'from-blue-50 to-indigo-50',
    description: 'Gouvernance et normalisation des gabarits',
    details: {
      title: 'Étape 1 : Création et Normalisation par Dany',
      content: [
        'Cette étape est réservée à Dany et aux gestionnaires de département. Elle établit les fondations de chaque gabarit PRIsME.',
        'Pour ajouter un nouveau gabarit, vous devez contacter Dany ou votre gestionnaire de département qui coordonnera la création.',
      ],
      governance: 'Gouvernance par département',
      keyPoints: [
        'Le gestionnaire de département, en collaboration avec Dany, crée les gabarits documents et formulaires holistiques',
        'Les gabarits documents et formulaires sont automatiquement liés : un bon document nécessite un formulaire de saisie rigoureux',
        'Dany crée ensuite un gabarit document normé selon les standards ITIL, COBIT, NIST ou autres cadres reconnus',
        "L'IA analyse le gabarit document normé pour générer 5 niveaux de qualité (Ad hoc à Optimized)",
        'Les normes représentent les standards de haut niveau à atteindre - c\'est la base d\'un gabarit officiel et professionnel',
      ],
      important: 'Cette étape garantit que tous les gabarits PRIsME respectent les meilleures pratiques et standards internationaux.',
    },
  },
  {
    id: 2,
    title: 'Remplir le formulaire',
    shortTitle: 'Remplir le formulaire',
    icon: FileText,
    color: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
    description: 'Complétion rigoureuse du formulaire',
    details: {
      title: 'Étape 2 : Remplir le Formulaire',
      content: [
        "C'est ici que les opérations officielles commencent pour vous. Si le gabarit document est créé, vous devez compléter le gabarit formulaire holistique associé.",
      ],
      keyPoints: [
        'Allez dans la section "Gabarits formulaire"',
        'Sélectionnez le gabarit formulaire correspondant au gabarit document que vous souhaitez utiliser',
        'Complétez le formulaire avec rigueur et analyse : un gabarit formulaire incomplet ou superficiel compromet tout le processus',
        'Une fois le gabarit formulaire complété à 100%, vous pouvez l\'ajuster dans l\'onglet "Formulaires" si nécessaire',
        'La qualité du gabarit formulaire détermine directement la qualité du document final généré',
      ],
      important: 'Rigueur et analyse sont essentielles : sans un gabarit formulaire complet et précis, le processus perd toute sa valeur.',
    },
  },
  {
    id: 3,
    title: 'Génération document',
    shortTitle: 'Génération document',
    icon: CheckCircle,
    color: 'from-purple-500 to-pink-600',
    bgGradient: 'from-purple-50 to-pink-50',
    description: 'Génération IA avec standards normés',
    details: {
      title: 'Étape 3 : Génération document',
      content: [
        "Dans l'onglet 'Gabarits documents', cliquez sur 'Créer document' pour le gabarit désiré.",
        "Il est essentiel d'être formé pour livrer un document de qualité. Cliquez sur 'Ouvrir l'espace' pour apprendre et comprendre le gabarit document.",
      ],
      keyPoints: [
        "Sélectionnez le client pour lequel vous créez le document",
        "Choisissez le gabarit formulaire compatible avec le gabarit document",
        "Si plusieurs prises de besoin existent pour le même gabarit, vous pouvez simuler différents scénarios - le concept est conçu pour cela",
        "Sélectionnez le niveau de qualité (1 à 5) normé ITIL/COBIT selon les besoins du client",
        "Lancez la création : tout est envoyé à l'IA pour analyse et standardisation selon les hauts standards",
        "Le document est généré automatiquement selon le gabarit formulaire complété",
      ],
      important: "La formation est cruciale : utilisez 'Ouvrir l'espace' pour comprendre chaque gabarit document avant de le créer.",
    },
  },
  {
    id: 4,
    title: 'Expertise humaine',
    shortTitle: 'Expertise humaine',
    icon: Edit,
    color: 'from-amber-500 to-orange-600',
    bgGradient: 'from-amber-50 to-orange-50',
    description: 'Valeur ajoutée humaine et expertise',
    details: {
      title: 'Étape 4 : Expertise humaine',
      content: [
        "C'est ici que se trouve la vraie différence et la richesse de BZ : l'expertise humaine et la valeur de nos employés.",
        'Dans la section "Liste des documents créés", l\'expert humain analyse le contenu généré et l\'ajuste selon son expertise et son jugement professionnel.',
      ],
      keyPoints: [
        'Tout le contenu est éditable pour permettre les ajustements nécessaires',
        "Les modifications doivent respecter le cadre et les normes établies du document",
        "Ne pas respecter les normes brise la cohérence et la valeur des niveaux et standards",
        "L'expertise humaine transforme un document généré en un document de valeur ajoutée",
        "C'est cette étape qui distingue BZ : l'intelligence artificielle génère, l'intelligence humaine perfectionne",
      ],
      important: 'Respectez toujours le cadre normatif : c\'est ce qui garantit la cohérence et la valeur professionnelle du document.',
    },
  },
  {
    id: 5,
    title: 'Présentation client',
    shortTitle: 'Livraison',
    icon: Eye,
    color: 'from-green-500 to-emerald-600',
    bgGradient: 'from-green-50 to-emerald-50',
    description: 'Livraison dans l\'espace client',
    details: {
      title: 'Étape 5 : Présentation au Client',
      content: [
        "Une fois le document approuvé, cochez la case 'Afficher dans PRIsME client'.",
        'Par magie, le document apparaîtra dans l\'espace du client dans la section "Liste des documents créés".',
      ],
      keyPoints: [
        "Présentez le document en mode vue via l'espace client uniquement",
        "Ne montrez JAMAIS le PRIsME Master BZ au client - c'est la richesse de l'entreprise",
        "PRIsME Master est le backstage, comme dans un show : c'est là que tout s'opère",
        "Si le client voit le backstage, cela change toute la perception : il se concentrera sur la mécanique et le technique",
        "Trop montrer gâche le spectacle et la raison d'être du document",
        "L'important : que le client gagne et évolue, pas qu'il comprenne la mécanique interne",
      ],
      important: "Protégez le backstage : PRIsME Master est notre avantage concurrentiel. Le client doit voir le résultat, pas le processus.",
    },
  },
];

export default function MethodologyFlowchart() {
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  return (
    <>
      <div className="bg-gradient-to-r from-bz-dark via-bz-blue to-indigo-700 text-white rounded-2xl p-8 shadow-xl mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center shadow-inner">
            <FileText className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">Méthodologie PRIsME - Gestion Documentaire</h2>
            <p className="text-base text-white/90 leading-relaxed">
              Processus structuré en 5 étapes pour créer des documents normés de haute qualité, 
              alliant intelligence artificielle et expertise humaine.
            </p>
          </div>
        </div>

        {/* Flowchart interactif */}
        <div className="mt-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4 relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isPremium = step.id === 4;
              const isLast = index === steps.length - 1;
              
              return (
                <div key={step.id} className="relative">
                  <motion.div
                    onClick={() => setSelectedStep(step.id)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className={`
                      relative w-full aspect-square p-4 rounded-xl
                      bg-gradient-to-br ${step.color}
                      shadow-lg hover:shadow-2xl
                      transition-all duration-300
                      border-2 ${isPremium ? 'border-yellow-300/50 hover:border-yellow-300' : 'border-white/20 hover:border-white/40'}
                      group cursor-pointer
                      ${isPremium ? 'ring-2 ring-yellow-400/30' : ''}
                      flex flex-col
                    `}
                  >
                    <div className="flex flex-col items-center justify-center gap-2 h-full">
                      <div className={`w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors ${isPremium ? 'ring-2 ring-yellow-300/50' : ''}`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-center flex-1 flex flex-col justify-center">
                        <div className="text-[10px] font-semibold text-white/80 mb-0.5">ÉTAPE {step.id}</div>
                        <h3 className="text-sm font-bold text-white mb-0.5">{step.shortTitle}</h3>
                        <p className="text-[10px] text-white/90 leading-tight px-1">{step.description}</p>
                      </div>
                      <div className="flex items-center gap-1 text-white/70 text-[10px]">
                        <Info className="w-3 h-3" />
                        <span>Détails</span>
                      </div>
                    </div>
                    
                    {/* Badge numéro */}
                    <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-white text-bz-blue font-bold text-xs flex items-center justify-center shadow-lg">
                      {step.id}
                    </div>

                    {/* Badge PREMIUM pour étape 4 */}
                    {isPremium && (
                      <div className="absolute -top-2 -right-2 flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full shadow-lg">
                        <span className="text-[9px] font-bold text-white">⭐ PREMIUM</span>
                      </div>
                    )}
                  </motion.div>

                  {/* Flèche sharp entre les étapes (desktop) */}
                  {!isLast && (
                    <>
                      {/* Flèche horizontale pour desktop */}
                      <div className="hidden lg:block absolute top-1/2 -right-2 -translate-y-1/2 z-10" style={{ right: '-0.5rem' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                          <defs>
                            <linearGradient id={`arrowGrad${step.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="rgba(255,255,255,0.5)" stopOpacity="1" />
                              <stop offset="100%" stopColor="rgba(255,255,255,0.95)" stopOpacity="1" />
                            </linearGradient>
                            <filter id={`arrowShadow${step.id}`} x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
                              <feOffset dx="0" dy="1" result="offsetblur"/>
                              <feComponentTransfer>
                                <feFuncA type="linear" slope="0.4"/>
                              </feComponentTransfer>
                              <feMerge>
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <path
                            d="M 2 12 L 18 12 M 18 12 L 13 7 M 18 12 L 13 17"
                            stroke={`url(#arrowGrad${step.id})`}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="miter"
                            fill="none"
                            filter={`url(#arrowShadow${step.id})`}
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                          />
                        </svg>
                      </div>
                      
                      {/* Flèche verticale pour tablette */}
                      <div className="hidden md:block lg:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 z-10">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                          <defs>
                            <linearGradient id={`arrowGradV${step.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgba(255,255,255,0.5)" stopOpacity="1" />
                              <stop offset="100%" stopColor="rgba(255,255,255,0.95)" stopOpacity="1" />
                            </linearGradient>
                            <filter id={`arrowShadowV${step.id}`} x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceAlpha" stdDeviation="1.5"/>
                              <feOffset dx="0" dy="1" result="offsetblur"/>
                              <feComponentTransfer>
                                <feFuncA type="linear" slope="0.4"/>
                              </feComponentTransfer>
                              <feMerge>
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <path
                            d="M 12 2 L 12 18 M 12 18 L 7 13 M 12 18 L 17 13"
                            stroke={`url(#arrowGradV${step.id})`}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="miter"
                            fill="none"
                            filter={`url(#arrowShadowV${step.id})`}
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                          />
                        </svg>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

                  {/* Flèches de connexion (mobile) */}
          <div className="md:hidden mt-3 flex items-center justify-center gap-2">
            {steps.slice(0, -1).map((_, index) => (
              <div key={index} className="flex flex-col items-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg">
                  <defs>
                    <linearGradient id={`arrowGradM${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0.5)" stopOpacity="1" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0.95)" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 12 2 L 12 18 M 12 18 L 7 13 M 12 18 L 17 13"
                    stroke={`url(#arrowGradM${index})`}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="miter"
                    fill="none"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                  />
                </svg>
              </div>
            ))}
          </div>
        </div>

        {/* Légende */}
        <div className="mt-6 pt-6 border-t border-white/20">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/80">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-400"></div>
              <span>Création & Normalisation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
              <span>Remplir le Formulaire</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-400"></div>
              <span>Génération IA</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
              <span>Expertise Humaine</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
              <span>Livraison Client</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal explicative */}
      <AnimatePresence>
        {selectedStep !== null && (
          <MethodologyStepModal
            isOpen={selectedStep !== null}
            onClose={() => setSelectedStep(null)}
            step={steps.find(s => s.id === selectedStep)!}
          />
        )}
      </AnimatePresence>
    </>
  );
}

